import {
  Button,
  Container,
  Fade,
  Grid,
  IconButton,
  Modal,
} from '@material-ui/core'
import {
  Apps,
  Close,
  FormatListBulleted,
  ImportExport,
  Search,
} from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  deleteProgram,
  listUserPrograms,
} from '../../../actions/programActions'
import filterIcon from '../../../assets/img/icons/filter.svg'
import { SIDE_NAV_CLOSE } from '../../../constants/navigationConstants'
import {
  PROGRAM_CREATE_RESET,
  PROGRAM_DELETE_RESET,
  PROGRAM_UPDATE_RESET,
} from '../../../constants/programConstants'
import machineList from '../../../data/machines'
import { useInput } from '../../../hooks/useInput'
import ActionsMenu from '../../ActionsMenu/ActionsMenu'
import AutoCompleteSelect from '../../Form/AutoCompleteSelect'
import SearchInput from '../../Form/SearchInput'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'
import AddEditProgram from '../../Program/AddEditProgram'
import ToastMessage from '../../ToastMessage/ToastMessage'
import ProgramGrid from './ProgramGrid'
import ProgramTable from './ProgramTable'

const ProgramLibrary = () => {
  const dispatch = useDispatch()

  const { bind: bindSearch, value: searchValue } = useInput('')
  const { value: model, setValue: setModel } = useInput('')
  const [viewType, setViewType] = useState('table')
  const [showFilters, setShowFilters] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [addProgramToast, setAddProgramToast] = useState(false)
  const [updateProgramToast, setUpdateProgramToast] = useState(false)
  const [updatedProgramName, setUpdatedProgramName] = useState('')
  const [createProgramName, setCreateProgramName] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [programToDelete, setProgramToDelete] = useState('')
  const [deleteProgramToast, setDeleteProgramToast] = useState(false)
  const [showAddEditProgram, setShowAddEditProgram] = useState(false)
  const [programToEdit, setProgramToEdit] = useState(undefined)
  const [sortValue, setSortValue] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [sorting, setSorting] = useState(false)
  const [filteredPrograms, setFilteredPrograms] = useState('')

  const { userInfo } = useSelector((state) => state.userLogin)
  const { loading, error, programs } = useSelector((state) => state.programList)
  const { program: createdProgram, success: createProgramSuccess } =
    useSelector((state) => state.programCreate)
  const { success: programUpdateSuccess, program: updatedProgram } =
    useSelector((state) => state.programUpdate)
  const { success: successDelete } = useSelector((state) => state.programDelete)

  useEffect(() => {
    if (searchValue !== '') {
      setFilteredPrograms(
        programs.filter((x) =>
          x.name.toLowerCase().includes(searchValue.toLowerCase())
        )
      )
    } else {
      setFilteredPrograms(programs)
    }
  }, [searchValue, setFilteredPrograms, programs])

  useEffect(() => {
    setSorting(true)

    if (sortValue === 'az') {
      setFilteredPrograms(
        filteredPrograms.sort((a, b) => a.name.localeCompare(b.name))
      )
    }

    if (sortValue === 'za') {
      setFilteredPrograms(
        filteredPrograms.sort((a, b) => b.name.localeCompare(a.name))
      )
    }

    if (sortValue === 'lastUpdated') {
      setFilteredPrograms(
        filteredPrograms.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      )
    }

    if (sortValue === 'newest') {
      setFilteredPrograms(
        filteredPrograms.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      )
    }

    setTimeout(() => setSorting(false), 200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilteredPrograms, sortValue, setSorting])

  useEffect(() => {
    if (model !== '') {
      setFilteredPrograms(programs.filter((x) => x.machineModel === model))
    } else {
      setFilteredPrograms(programs)
    }
  }, [model, setFilteredPrograms, programs])

  useEffect(() => {
    dispatch(listUserPrograms(userInfo._id))
    dispatch({ type: SIDE_NAV_CLOSE })

    if (createProgramSuccess) {
      window.scrollTo(0, 0)
      setAddProgramToast(true)
      setCreateProgramName(createdProgram.name)
      dispatch({ type: PROGRAM_CREATE_RESET })
    }

    if (programUpdateSuccess) {
      window.scrollTo(0, 0)
      setUpdateProgramToast(true)
      setUpdatedProgramName(updatedProgram.name)
      setProgramToEdit('')
      dispatch({ type: PROGRAM_UPDATE_RESET })
      dispatch({ type: SIDE_NAV_CLOSE })
    }

    if (successDelete) {
      window.scrollTo(0, 0)
      setDeleteProgramToast(true)
      dispatch({ type: PROGRAM_DELETE_RESET })
    }
  }, [
    programUpdateSuccess,
    updatedProgram,
    createProgramSuccess,
    dispatch,
    successDelete,
    userInfo,
    createdProgram,
  ])

  const handleClose = () => {
    setProgramToEdit('')
    setShowAddEditProgram(false)
  }

  const confirmDeleteProgram = () => {
    dispatch(deleteProgram(programToDelete._id))
    setShowDeleteModal(false)
  }

  const handleAddProgram = () => {
    setProgramToEdit('')
    setShowAddEditProgram(true)
  }

  const handleClear = () => {
    setModel('')
  }

  return (
    <div className="library gray-bg">
      <Container className="p-b-100">
        <h3 className="page-title accent-left">Program library</h3>

        <div className="flex flex-end m-b-16" style={{ height: 33 }}>
          {showSearch ? (
            <SearchInput bindValue={bindSearch} setShowSearch={setShowSearch} />
          ) : (
            <IconButton onClick={() => setShowSearch(true)}>
              <Search />
            </IconButton>
          )}
          <ActionsMenu
            icon={<ImportExport />}
            options={[
              { onClick: () => setSortValue('az'), label: 'A - Z' },
              { onClick: () => setSortValue('za'), label: 'Z - A' },
              { onClick: () => setSortValue('newest'), label: 'Newest' },
              {
                onClick: () => setSortValue('lastUpdated'),
                label: 'Last updated',
              },
            ]}
          ></ActionsMenu>
          <IconButton
            className="m-l-5"
            style={{ width: 32, height: 33 }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <img style={{ width: 20 }} src={filterIcon} alt="Filter" />
          </IconButton>
          {viewType === 'table' ? (
            <IconButton className="m-l-5" onClick={() => setViewType('grid')}>
              <Apps />
            </IconButton>
          ) : (
            <IconButton className="m-l-5" onClick={() => setViewType('table')}>
              <FormatListBulleted />
            </IconButton>
          )}
          <Button
            className="m-l-24"
            variant="contained"
            color="primary"
            onClick={handleAddProgram}
          >
            Add program
          </Button>
        </div>

        {showFilters && (
          <div className="filter-bar">
            <div className="flex space-between">
              <label className="fs14 strong">Filters</label>
              <span
                className="link fs12"
                rel="noopener noreferrer"
                target="_blank"
                onClick={handleClear}
              >
                Clear
              </span>
            </div>
            <div className="form-container p-l-0 p-r-0">
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <AutoCompleteSelect
                    label="Model"
                    name="model"
                    options={machineList}
                    value={model}
                    setValue={setModel}
                    minWidth={272}
                  />
                </Grid>
              </Grid>
            </div>
          </div>
        )}

        {loading && <Loader />}

        {error && <Message variant="error">{error}</Message>}

        {!loading && !error && viewType === 'table'
          ? filteredPrograms !== '' && (
              <ProgramTable
                programs={filteredPrograms}
                setProgramToEdit={setProgramToEdit}
                setShowAddEditProgram={setShowAddEditProgram}
                setProgramToDelete={setProgramToDelete}
                setShowDeleteModal={setShowDeleteModal}
              />
            )
          : filteredPrograms !== '' && (
              <ProgramGrid
                programs={filteredPrograms}
                setProgramToEdit={setProgramToEdit}
                setShowAddEditProgram={setShowAddEditProgram}
                setProgramToDelete={setProgramToDelete}
                setShowDeleteModal={setShowDeleteModal}
              />
            )}
      </Container>

      <Modal open={showAddEditProgram} onClose={handleClose}>
        <Fade in={showAddEditProgram}>
          <div className="modal-window full">
            <div className="modal-header">
              <div>{programToEdit === '' ? 'Add new' : 'Edit'} program</div>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <AddEditProgram
                setShowAddProgram={setShowAddEditProgram}
                setProgramToEdit={setProgramToEdit}
                programToEdit={programToEdit}
              />
            </div>
          </div>
        </Fade>
      </Modal>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Fade in={showDeleteModal}>
          <div className="modal-window">
            <div className="modal-header">
              <div>Delete program?</div>
              <IconButton onClick={() => setShowDeleteModal(false)}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <h4 className="m-b-32">Delete program?</h4>
              <p>
                Are you sure you want to delete {programToDelete.name}? This may
                impact any projects that are currently using this program.
              </p>
              <div className="actions m-t-40 flex-end flex">
                <Button
                  color="primary"
                  variant="outlined"
                  className="m-r-24"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={confirmDeleteProgram}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Program added</p>
            <p className="fs14 m-b-0">
              You successfully created {createProgramName}.
            </p>
          </>
        }
        open={addProgramToast}
        setOpen={setAddProgramToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Program updated</p>
            <p className="fs14 m-b-0">
              You successfully updated {updatedProgramName}.
            </p>
          </>
        }
        open={updateProgramToast}
        setOpen={setUpdateProgramToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Program deleted</p>
            <p className="fs14 m-b-0">
              You successfully deleted {programToDelete.name}.
            </p>
          </>
        }
        open={deleteProgramToast}
        setOpen={setDeleteProgramToast}
      />
    </div>
  )
}

export default ProgramLibrary
